import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { IconDate, IconLocation, IconReviewStar } from '../../components';

import css from './ProfileDetails.module.css';

const PackageIcon = () => (
  <svg
    className={css.strokeBoxIcon}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path d="M21 8.5 12 3 3 8.5v7L12 21l9-5.5v-7Z" />
    <path d="M12 12v9M3 8.5l9 3.5 9-3.5M7.5 6 16.5 11" />
  </svg>
);

const TagIcon = () => (
  <svg
    className={css.strokeBoxIcon}
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    focusable="false"
  >
    <path d="M20.6 13.05 11 3.4H4v7l9.6 9.6a1.5 1.5 0 0 0 2.12 0l4.88-4.88a1.5 1.5 0 0 0 0-2.12Z" />
    <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

/**
 * Stat boxes under the profile name: rating, sales, active listings, location, member since.
 *
 * @component
 */
const ProfileDetails = props => {
  const { user, activeListingsCount, showItemsSold = false, itemsSoldCount = 0, className } = props;
  const intl = useIntl();

  const publicData = user?.attributes?.profile?.publicData || {};
  const metadata = user?.attributes?.profile?.metadata || {};
  const createdAt = user?.attributes?.createdAt;
  const location =
    typeof publicData.profileLocation === 'string' ? publicData.profileLocation.trim() : '';
  const reviewAverage = metadata.reviewAverage;
  const reviewCount = metadata.reviewCount;
  const hasRating =
    typeof reviewAverage === 'number' && typeof reviewCount === 'number' && reviewCount > 0;
  const showActiveListings =
    typeof activeListingsCount === 'number' && Number.isFinite(activeListingsCount);

  const memberYear =
    createdAt instanceof Date || (createdAt && !Number.isNaN(Date.parse(createdAt)))
      ? intl.formatDate(createdAt, { year: 'numeric' })
      : null;

  const hasAny = hasRating || showItemsSold || showActiveListings || !!location || !!memberYear;
  if (!hasAny) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      {hasRating ? (
        <div className={css.box}>
          <span className={css.ratingValue}>{reviewAverage}</span>
          <IconReviewStar className={css.star} isFilled isAriaHidden />
          <span className={css.muted}>
            <FormattedMessage id="ProfilePage.statReviewsCount" values={{ count: reviewCount }} />
          </span>
        </div>
      ) : null}

      {showItemsSold ? (
        <div className={css.box}>
          <PackageIcon />
          <span className={css.boxText}>
            <FormattedMessage id="ProfilePage.statSalesCount" values={{ count: itemsSoldCount }} />
          </span>
        </div>
      ) : null}

      {showActiveListings ? (
        <div className={css.box}>
          <TagIcon />
          <span className={css.boxText}>
            <FormattedMessage
              id="ProfilePage.statActiveListingsCount"
              values={{ count: activeListingsCount }}
            />
          </span>
        </div>
      ) : null}

      {location ? (
        <div className={css.box}>
          <IconLocation className={css.boxIcon} />
          <span className={css.boxText}>{location}</span>
        </div>
      ) : null}

      {memberYear ? (
        <div className={css.box}>
          <IconDate className={css.boxIcon} />
          <span className={css.boxText}>
            <FormattedMessage id="ProfilePage.memberSinceShort" values={{ date: memberYear }} />
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileDetails;
